import { z } from 'zod';

/**
 * THE FILES TAB CONTRACT (2026-11-02) — the production's agreements, permits and
 * location documents, one file for the API, the SPA and the tests.
 *
 * A FILE IS KEPT, NEVER SERVED, UNTIL FILE CHECKING EXISTS. Every upload is stored
 * `QUARANTINED` and nothing sets it `PASSED` yet (no malware scanner runs), so no
 * route signs an original: `downloadable` is the literal `false`, and the SPA says
 * the file is kept privately instead of offering a link that cannot work (the owner's
 * References decision of 2026-11-02, applied to Files).
 *
 * The Files tab lists `purpose IS NULL` only — a References library upload is a file
 * too, but it belongs to the library.
 */

/**
 * What a file is, from its STORED (sniffed) MIME type, never the browser's claim
 * (`backend/src/modules/operations/project-file-type.ts`). OTHER covers rows stored
 * before the type was sniffed, whose MIME type is whatever the browser sent then.
 */
export const ProjectFileKind = z.enum(['PDF', 'DOCUMENT', 'SPREADSHEET', 'PRESENTATION', 'IMAGE', 'OTHER']);
export type ProjectFileKind = z.infer<typeof ProjectFileKind>;

/** A filter chip: "PDFs (3)". */
export const PROJECT_FILE_KIND_PLURALS: Readonly<Record<ProjectFileKind, string>> = Object.freeze({
  PDF: 'PDFs',
  DOCUMENT: 'Documents',
  SPREADSHEET: 'Spreadsheets',
  PRESENTATION: 'Presentations',
  IMAGE: 'Images',
  OTHER: 'Other',
});

/** One file's kind in a word: "PDF", "Spreadsheet". */
export const PROJECT_FILE_KIND_LABELS: Readonly<Record<ProjectFileKind, string>> = Object.freeze({
  PDF: 'PDF',
  DOCUMENT: 'Document',
  SPREADSHEET: 'Spreadsheet',
  PRESENTATION: 'Presentation',
  IMAGE: 'Image',
  OTHER: 'Other file',
});

/** The list's orders. RECENT is the default and never written into the address. */
export const ProjectFileSort = z.enum(['RECENT', 'OLDEST', 'NAME', 'SIZE']);
export type ProjectFileSort = z.infer<typeof ProjectFileSort>;

/**
 * The upload's one form field besides the file (`POST …/files`, multipart).
 *
 * `clientRequestId` is minted once per FILE in the browser and kept: a retry after a
 * lost answer sends the same key, and the server answers with the file it already
 * stored instead of storing a second one. Optional, because a page cached in a
 * browser before 2026-11-02 sends only the file; such an upload is simply not
 * idempotent, exactly as before.
 */
export const UploadProjectFileFields = z.object({
  clientRequestId: z.uuid().optional(),
});
export type UploadProjectFileFields = z.infer<typeof UploadProjectFileFields>;

/** `GET /v1/projects/:projectId/files` — one row, newest first. */
export interface ProjectFileView {
  readonly id: string;
  /** As uploaded, without any path (`displayFilename`). Display only. */
  readonly originalFilename: string;
  /** The canonical type for the sniffed content. */
  readonly mimeType: string;
  readonly kind: ProjectFileKind;
  readonly byteSize: number;
  /** QUARANTINED for every file today; PASSED once a check exists and passes. */
  readonly validationStatus: string;
  /** No route serves an original until file checking exists. */
  readonly downloadable: false;
  readonly uploadedByUserId: number;
  /** The uploader's name from the platform's directory; null when it is not known. */
  readonly uploadedBy: { readonly userId: number; readonly displayName: string | null };
  readonly createdAt: string;
  /** The file this one replaced. Nothing sets it yet: no route uploads a replacement. */
  readonly supersedesFileId: string | null;
}

/** `GET /v1/projects/:projectId/files/meta` — what the upload controls need, from the server. */
export interface ProjectFilesMeta {
  readonly upload: {
    /** The API's one limit (`UPLOAD_MAX_BYTES`), never a number typed into the SPA. */
    readonly maxBytes: number;
    /** Extensions without the dot, lower case: the sniffed allowlist. */
    readonly acceptedExtensions: readonly string[];
    /** False on a plan without media storage: the page offers the plan instead of the picker. */
    readonly allowed: boolean;
  };
  /** A lapsed production: everything stays readable and nothing can be uploaded or deleted. */
  readonly readOnly: boolean;
}

/** `DELETE /v1/projects/:projectId/files/:fileId` — the row is soft-deleted and its object kept. */
export interface ProjectFileDeleted {
  readonly id: string;
}
